type OnEngagement = (params: { engagementMsec: number }) => void

type EngagementTracker = { stop: () => void; flush: () => void }

export const initEngagementTracking = (onEngagement: OnEngagement): EngagementTracker => {
  let segmentStart: number | null =
    document.visibilityState === 'visible' ? performance.now() : null
  let accumulatedMs = 0
  let lastEmittedMs = 0
  let heartbeatId: ReturnType<typeof setInterval> | null = null

  const emit = (delta: number): void => {
    onEngagement({ engagementMsec: Math.round(delta) })
  }

  const startHeartbeat = (): void => {
    if (heartbeatId !== null) return
    heartbeatId = setInterval(() => {
      if (segmentStart === null) return
      const total = accumulatedMs + (performance.now() - segmentStart)
      const delta = total - lastEmittedMs
      if (delta > 0) {
        emit(delta)
        lastEmittedMs = total
      }
    }, 10_000)
  }

  const stopHeartbeat = (): void => {
    if (heartbeatId === null) return
    clearInterval(heartbeatId)
    heartbeatId = null
  }

  const onVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') {
      if (segmentStart !== null) {
        accumulatedMs += performance.now() - segmentStart
        segmentStart = null
      }
      stopHeartbeat()
      const delta = accumulatedMs - lastEmittedMs
      if (delta > 0) {
        emit(delta)
        lastEmittedMs = accumulatedMs
      }
    } else {
      segmentStart = performance.now()
      startHeartbeat()
    }
  }

  const flush = (): void => {
    stopHeartbeat()
    if (segmentStart !== null) {
      accumulatedMs += performance.now() - segmentStart
      segmentStart = null
    }
    const delta = accumulatedMs - lastEmittedMs
    if (delta > 0) {
      emit(delta)
    }
    accumulatedMs = 0
    lastEmittedMs = 0
    if (document.visibilityState === 'visible') {
      segmentStart = performance.now()
      startHeartbeat()
    }
  }

  const stop = (): void => {
    stopHeartbeat()
    document.removeEventListener('visibilitychange', onVisibilityChange)
  }

  document.addEventListener('visibilitychange', onVisibilityChange)

  if (segmentStart !== null) {
    startHeartbeat()
  }

  return { stop, flush }
}
